<template>
  <div>
    <label for="cache" class="block text-sm/6 font-medium text-gray-900 dark:text-white"
      >Cache</label
    >
    <div class="mt-2 grid grid-cols-1">
      <select
        id="cache"
        name="cache"
        v-model="selected"
        :disabled="loading"
        class="w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-white/10 dark:*:bg-gray-800 dark:disabled:bg-white/10"
        aria-describedby="cache-desc"
      >
        <option value="">None</option>
        <option v-for="cache in caches" :key="cache.id" :value="cache.id">
          {{ cache.name }}{{ cache.shared ? '' : ' (per runner)' }}
        </option>
      </select>
    </div>
    <p v-if="error" class="mt-2 text-sm text-red-600 dark:text-red-400">{{ error }}</p>
    <p
      v-else-if="warning"
      class="mt-2 text-sm text-yellow-700 dark:text-yellow-400"
      data-testid="cache-warning"
    >
      {{ warning }}
    </p>
    <p v-else id="cache-desc" class="mt-2 text-sm text-gray-500 dark:text-gray-400">
      S3 bucket for the GitLab CI <code>cache:</code> keyword. Manage caches on the
      <router-link
        :to="{ name: 'Caches' }"
        class="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
        >Caches</router-link
      >
      page.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { asProblem, listCaches, type RunnerCache } from '@/api/caches';

const props = defineProps<{
  /** The selected cache id, or an empty string for none. */
  modelValue: string;
  warning?: string;
}>();

const emit = defineEmits<{ (e: 'update:modelValue', value: string): void }>();

// v-model on the select re-applies the selection once the options have loaded.
const selected = computed({
  get: () => props.modelValue,
  set: (value: string) => emit('update:modelValue', value),
});

const caches = ref<RunnerCache[]>([]);
const loading = ref(true);
const error = ref('');

onMounted(async () => {
  try {
    caches.value = await listCaches();
  } catch (failure) {
    const e = asProblem(failure);
    error.value = e?.response?.data?.detail || e?.message || 'Failed to load caches.';
  } finally {
    loading.value = false;
  }
});
</script>
